/*
* === MyDocumentsPage.js (Upgraded Vault with Smart Previews & Validation) ===
*/
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Container, Typography, Card, Box, Alert, Button, Grid, Paper, IconButton,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, FormControl, InputLabel, Select, MenuItem, CardContent, CardActions,
    Divider, Snackbar, InputAdornment, Tooltip, Chip, Checkbox, FormControlLabel,
    useTheme, useMediaQuery
} from '@mui/material';

import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// React-PDF Imports
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

// 🚨 EXECUTABLE CODE MUST GO AFTER ALL IMPORTS 🚨
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- Helper: Preview URL ---
const getPreviewUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    
    // Route the request THROUGH our secure backend proxy
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://brightsparc-api-520983012715.africa-south1.run.app';
    
    // Clean the path to ensure it formats correctly (e.g., stripping existing /vault/ prefixes so it doesn't double up)
    const cleanPath = path.replace(/\\/g, '/').replace(/^\/?vault\//, '');
    
    return `${baseUrl}/vault/${cleanPath}`;
};

// --- Helper: Check Expiry Status ---
const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Expired', color: 'error' };
    if (diffDays <= 30) return { label: `Expires in ${diffDays} days`, color: 'warning' };
    return { label: `Expires: ${expiry.toLocaleDateString()}`, color: 'default' };
};

// --- Document Card ---
const DocumentCard = ({ doc, onDelete, onPreview, onEdit, onDownload, selected, onSelect }) => {
    const isPdf = doc.FileName.toLowerCase().endsWith('.pdf');
    const isImage = /\.(jpg|jpeg|png)$/i.test(doc.FileName);
    const expiryStatus = getExpiryStatus(doc.ExpiryDate);
    const previewUrl = getPreviewUrl(doc.UploadPath);

    return (
        <Card 
            sx={{ 
                height: '100%', display: 'flex', flexDirection: 'column', transition: '0.3s', position: 'relative',
                border: selected ? '2px solid #1976d2' : '1px solid transparent',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } 
            }}
        >
            <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
                <Checkbox 
                    checked={selected} onChange={() => onSelect(doc.Id)}
                    icon={<CheckBoxOutlineBlankIcon sx={{ color: 'action.disabled' }} />}
                    checkedIcon={<CheckBoxIcon color="primary" />}
                />
            </Box>

            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                {doc.IsVerified ? (
                    <Tooltip title="Verified by Admin">
                        <VerifiedIcon color="success" />
                    </Tooltip>
                ) : (
                    <Tooltip title="Pending Review">
                        <PendingActionsIcon color="warning" />
                    </Tooltip>
                )}
            </Box>

            <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 4, pb: 1, cursor: 'pointer' }} onClick={() => onSelect(doc.Id)}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    {isImage ? (
                        <Box component="img" src={previewUrl} alt="preview" sx={{ width: 60, height: 80, objectFit: 'cover', borderRadius: 1, mb: 1, border: '1px solid #eee' }} />
                    ) : (
                        <DescriptionIcon color={isPdf ? "error" : "primary"} sx={{ fontSize: 60, mb: 1, opacity: 0.8 }} />
                    )}
                </Box>
                
                <Typography variant="subtitle1" fontWeight="bold" noWrap title={doc.DocumentLabel}>{doc.DocumentLabel || doc.FileName}</Typography>
                <Chip label={doc.DocumentType.replace(/_/g, ' ')} size="small" sx={{ mt: 1, fontSize: '0.7rem', height: 20 }} />

                {expiryStatus && (
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                        <Chip icon={<EventIcon style={{ fontSize: 14 }} />} label={expiryStatus.label} color={expiryStatus.color} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                    </Box>
                )}
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'space-between', px: 1 }}>
                <Tooltip title="Download"><IconButton size="small" onClick={() => onDownload(doc)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                <Button size="small" startIcon={<VisibilityIcon />} onClick={() => onPreview(doc)}>View</Button>
                <Box>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(doc)} disabled={doc.IsVerified}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(doc)} color="error" disabled={doc.IsVerified}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
            </CardActions>
        </Card>
    );
};

// --- Add/Edit Modal ---
const AddDocumentModal = ({ open, onClose, token, refreshDocuments, showSnackbar, editingDoc = null, maxSizeMB = 5 }) => {
    const [documentType, setDocumentType] = useState('OTHER');
    const [documentLabel, setDocumentLabel] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    useEffect(() => {
        if (open) {
            setDocumentType(editingDoc ? editingDoc.DocumentType : 'OTHER');
            setDocumentLabel(editingDoc ? (editingDoc.DocumentLabel || '') : '');
            setExpiryDate(editingDoc?.ExpiryDate ? editingDoc.ExpiryDate.split('T')[0] : '');
            setFile(null);
        }
    }, [open, editingDoc]);

    const validateAndSetFile = (selectedFile) => {
        if (!selectedFile) return;
        
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(selectedFile.type)) {
            showSnackbar('Invalid file type. Only PDF, JPG, and PNG are allowed.', 'error');
            return;
        }

        if (selectedFile.size > maxSizeMB * 1024 * 1024) {
            showSnackbar(`File is too large. Maximum size is ${maxSizeMB}MB.`, 'error');
            return;
        }

        setFile(selectedFile);
    };

    const handleDrop = (e) => {
        if (editingDoc) return;
        e.preventDefault(); e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) validateAndSetFile(e.dataTransfer.files[0]);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) validateAndSetFile(e.target.files[0]);
    };

    const handleDrag = (e) => {
        if (editingDoc) return;
        e.preventDefault(); e.stopPropagation();
        setDragActive(e.type === "dragenter" || e.type === "dragover");
    };

    const handleSubmit = async () => {
        if (!documentLabel || (!editingDoc && !file)) return;
        setUploading(true);

        try {
            if (editingDoc) {
                await api.put(`/applicant/documents/${editingDoc.Id}`, { documentType, documentLabel, expiryDate }, { headers: { Authorization: `Bearer ${token}` } });
                showSnackbar('Document details updated.');
            } else {
                const formData = new FormData();
                formData.append('document', file);
                formData.append('documentType', documentType);
                formData.append('documentLabel', documentLabel);
                formData.append('expiryDate', expiryDate);

                await api.post('/applicant/documents', formData, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } });
                showSnackbar('Document uploaded successfully!');
            }
            refreshDocuments();
            onClose();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Operation failed.', 'error');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{editingDoc ? 'Edit Document Details' : 'Upload New Document'}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <TextField autoFocus margin="dense" label="Document Label (e.g., '2024 Transcript')" fullWidth value={documentLabel} onChange={(e) => setDocumentLabel(e.target.value)} sx={{ mb: 2 }} />
                    <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                        <InputLabel>Document Type</InputLabel>
                        <Select value={documentType} label="Document Type" onChange={(e) => setDocumentType(e.target.value)}>
                            <MenuItem value="ID_DOCUMENT">ID Document</MenuItem>
                            <MenuItem value="ACADEMIC_TRANSCRIPT">Academic Transcript</MenuItem>                        
                            <MenuItem value="PROOF_OF_ADDRESS">Proof of Address</MenuItem>                        
                            <MenuItem value="PROOF_OF_REGISTRATION">Proof of Registration</MenuItem>
                            <MenuItem value="ACCEPTANCE_LETTER">Acceptance Letter/Proof of Application</MenuItem>
                            <MenuItem value="PROOF_OF_DEPENDENCY">Proof of Dependency</MenuItem>
                            <MenuItem value="PAYSLIP_MEMBER">Payslip (Member)</MenuItem>
                            <MenuItem value="NUM_MEMBER_ID">Member ID</MenuItem>
                            <MenuItem value="TEBA_FORM">TEBA Nomination Form</MenuItem>
                            <MenuItem value="OTHER">Other/General</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Certification Expiry Date (Optional)" type="date" fullWidth InputLabelProps={{ shrink: true }} value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} sx={{ mb: 3 }} />

                    {!editingDoc && (
                        /* 🚨 FIX: Applied Flexbox to ensure the label doesn't collapse! */
                        <Box 
                            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                            sx={{ 
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                border: '2px dashed', borderColor: dragActive ? 'primary.main' : 'grey.400', 
                                borderRadius: 2, p: 4, minHeight: 160, textAlign: 'center', 
                                bgcolor: dragActive ? 'action.hover' : 'background.paper', 
                                cursor: 'pointer', transition: 'all 0.2s' 
                            }}
                            component="label"
                        >
                            <input type="file" hidden onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png" />
                            <CloudUploadIcon sx={{ fontSize: 48, color: dragActive ? 'primary.main' : 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" color="text.primary">{file ? <strong>{file.name}</strong> : "Drag & drop your file here"}</Typography>
                            <Typography variant="caption" color="text.secondary">or click to browse (Max {maxSizeMB}MB | PDF, JPG, PNG)</Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={uploading || (!editingDoc && !file) || !documentLabel}>
                    {uploading ? <CircularProgress size={24} /> : (editingDoc ? 'Save Changes' : 'Upload')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Main Page ---
const MyDocumentsPage = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const { token } = useAuth();
    
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL');
    const [sortBy, setSortBy] = useState('newest');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    const [selectedIds, setSelectedIds] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
    };

    // 🚨 FIX: Dynamic Max Size State
    const [maxSizeMB, setMaxSizeMB] = useState(5);
    
    // 🚨 Add this mobile detector
    const theme = useTheme();
    const isMobilePreview = useMediaQuery(theme.breakpoints.down('md'));

    const fetchDocuments = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await api.get('/applicant/documents', { headers: { Authorization: `Bearer ${token}` } });
            setDocuments(response.data);
            setSelectedIds([]); 
        } catch (err) {
            showSnackbar('Failed to fetch documents.', 'error');
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Fetch dynamic max file size from public settings on load
    useEffect(() => { 
        fetchDocuments(); 
        api.get('/public/tier-limits')
           .then(res => {
               const sizeSetting = res.data.find(s => s.SettingKey === 'max_upload_size_mb');
               if (sizeSetting && sizeSetting.SettingValue) {
                   setMaxSizeMB(parseInt(sizeSetting.SettingValue, 10));
               }
           })
           .catch(() => console.log('Falling back to default max upload size.'));
    }, [fetchDocuments]);

    const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const processedDocuments = useMemo(() => {
        let result = documents.filter(doc => {
            const label = doc.DocumentLabel || '';
            const fileName = doc.FileName || '';
            const matchesSearch = label.toLowerCase().includes(searchTerm.toLowerCase()) || fileName.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesType = typeFilter === 'ALL' || doc.DocumentType === typeFilter;
            return matchesSearch && matchesType;
        });

        result.sort((a, b) => {
            if (sortBy === 'newest') return b.Id - a.Id;
            if (sortBy === 'oldest') return a.Id - b.Id;
            if (sortBy === 'name') return (a.DocumentLabel || a.FileName).localeCompare(b.DocumentLabel || b.FileName);
            if (sortBy === 'expiry') {
                if (!a.ExpiryDate) return 1;
                if (!b.ExpiryDate) return -1;
                return new Date(a.ExpiryDate) - new Date(b.ExpiryDate);
            }
            return 0;
        });

        return result;
    }, [documents, searchTerm, typeFilter, sortBy]);

    const handleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    const handleSelectAll = (e) => setSelectedIds(e.target.checked ? processedDocuments.map(d => d.Id) : []);

    const handleSingleDownload = async (doc) => {
        try {
            const url = getPreviewUrl(doc.UploadPath);
            const response = await fetch(url);
            if (!response.ok) throw new Error('Fetch failed');
            const blob = await response.blob();
            saveAs(blob, doc.FileName);
        } catch (e) {
            showSnackbar('Download failed. Ensure browser popups are allowed.', 'error');
        }
    };

    const handleBulkDownload = async () => {
        if (selectedIds.length === 0) return;
        setIsDownloading(true);
        const zip = new JSZip();
        const folder = zip.folder('my-documents');

        try {
            const docsToDownload = documents.filter(d => selectedIds.includes(d.Id));
            await Promise.all(docsToDownload.map(async (doc) => {
                const url = getPreviewUrl(doc.UploadPath);
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Failed to fetch ${doc.FileName}`);
                const blob = await response.blob();
                folder.file(doc.FileName, blob);
            }));
            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, 'BrightSparc_Documents.zip');
            showSnackbar('Documents downloaded successfully.');
        } catch (e) {
            showSnackbar('Bulk download failed.', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedDoc) return;
        try {
            await api.delete(`/applicant/documents/${selectedDoc.Id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchDocuments();
            showSnackbar('Document deleted successfully.');
            setIsDeleteModalOpen(false);
            setSelectedDoc(null);
        } catch (err) {
            showSnackbar('Failed to delete document. It may be locked by an admin.', 'error');
        }
    };

    const openAddModal = () => { setEditingDoc(null); setIsModalOpen(true); };
    const openEditModal = (doc) => { setEditingDoc(doc); setIsModalOpen(true); };
    const openPreview = (doc) => { setSelectedDoc(doc); setIsPreviewOpen(true); };

    const uniqueTypes = [...new Set(documents.map(d => d.DocumentType))];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold">Digital Vault</Typography>
                    <Typography variant="body1" color="text.secondary">Securely manage your personal documents.</Typography>
                </Box>
                <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={openAddModal}>
                    Add Document
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs="auto">
                        <FormControlLabel control={<Checkbox checked={selectedIds.length > 0 && selectedIds.length === processedDocuments.length} indeterminate={selectedIds.length > 0 && selectedIds.length < processedDocuments.length} onChange={handleSelectAll} />} label={selectedIds.length > 0 ? `${selectedIds.length} Selected` : "Select All"} />
                    </Grid>

                    {selectedIds.length > 0 && (
                        <Grid item xs="auto">
                            <Button variant="contained" color="secondary" startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />} onClick={handleBulkDownload} disabled={isDownloading}>
                                {isDownloading ? 'Zipping...' : 'Download Selected'}
                            </Button>
                        </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                        <TextField fullWidth size="small" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)} startAdornment={<InputAdornment position="start"><FilterListIcon /></InputAdornment>}>
                                <MenuItem value="ALL">All</MenuItem>
                                {uniqueTypes.map(t => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Sort</InputLabel>
                            <Select value={sortBy} label="Sort" onChange={(e) => setSortBy(e.target.value)} startAdornment={<InputAdornment position="start"><SortIcon /></InputAdornment>}>
                                <MenuItem value="newest">Newest</MenuItem>
                                <MenuItem value="oldest">Oldest</MenuItem>
                                <MenuItem value="name">Name</MenuItem>
                                <MenuItem value="expiry">Expiry</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
            ) : processedDocuments.length > 0 ? (
                <Grid container spacing={3}>
                    {processedDocuments.map((doc) => (
                        <Grid item key={doc.Id} xs={12} sm={6} md={4} lg={3}>
                            <DocumentCard doc={doc} selected={selectedIds.includes(doc.Id)} onSelect={handleSelect} onDelete={() => { setSelectedDoc(doc); setIsDeleteModalOpen(true); }} onEdit={() => openEditModal(doc)} onPreview={openPreview} onDownload={handleSingleDownload} />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed' }}>
                    <Typography color="text.secondary">No documents found.</Typography>
                </Paper>
            )}

            <AddDocumentModal open={isModalOpen} onClose={() => setIsModalOpen(false)} token={token} refreshDocuments={fetchDocuments} showSnackbar={showSnackbar} editingDoc={editingDoc} maxSizeMB={maxSizeMB} />

            <Dialog open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                    <WarningAmberIcon sx={{ mr: 1 }} /> Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete <strong>{selectedDoc?.DocumentLabel}</strong>?</Typography>
                    <Typography variant="caption" color="text.secondary">If an admin has already verified this document, you cannot delete it.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* 🚨 FIX: Smart Preview Modal using React-PDF Canvas */}
            <Dialog open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {selectedDoc?.DocumentLabel}
                    <Box>
                        {/* <Tooltip title="Download original file">
                            <IconButton href={selectedDoc ? getPreviewUrl(selectedDoc.UploadPath) : '#'} target="_blank" download><DownloadIcon /></IconButton>
                        </Tooltip> */}
                        <IconButton onClick={() => setIsPreviewOpen(false)}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, height: '80vh', bgcolor: 'grey.300', display: 'flex', flexDirection: 'column', alignItems: 'center', overflowY: 'auto' }}>
                    {selectedDoc && (
                        /\.(jpg|jpeg|png)$/i.test(selectedDoc.FileName) ? (
                            <img src={getPreviewUrl(selectedDoc.UploadPath)} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : (
                            <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                <Document
                                    file={getPreviewUrl(selectedDoc.UploadPath)}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    loading={<CircularProgress sx={{ my: 10 }} />}
                                    error={<Typography color="error" sx={{ my: 10 }}>Failed to load PDF. Please download it instead.</Typography>}
                                >
                                    <Paper elevation={4} sx={{ mb: 2 }}>
                                        {/* Renders the current page on a responsive canvas */}
                                        <Page 
                                            pageNumber={pageNumber} 
                                            width={Math.min(window.innerWidth * 0.9, 800)} // Responsive scaling
                                            renderTextLayer={false} 
                                        />
                                    </Paper>
                                </Document>
                                
                                {/* Pagination Controls */}
                                {numPages > 1 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, bgcolor: 'background.paper', p: 1, borderRadius: 2, boxShadow: 1 }}>
                                        <Button 
                                            disabled={pageNumber <= 1} 
                                            onClick={() => setPageNumber(prev => prev - 1)}
                                        >
                                            Previous
                                        </Button>
                                        <Typography variant="body2" fontWeight="bold">
                                            Page {pageNumber} of {numPages}
                                        </Typography>
                                        <Button 
                                            disabled={pageNumber >= numPages} 
                                            onClick={() => setPageNumber(prev => prev + 1)}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default MyDocumentsPage;